import type { BaseQueryFn, EndpointBuilder } from "@reduxjs/toolkit/query";
import type { ServerResponse } from "@/types/network";
import { apiSlice } from "@features/application/api-slice";
import { personalAccessTokensRoutes } from "@features/personal-access-tokens/personal-access-tokens-routes";
import type {
  AdminIssuePersonalAccessTokenRequest,
  AdminListPersonalAccessTokensRequest,
  AdminRevokePersonalAccessTokenRequest,
  IssuedPersonalAccessToken,
  IssuePersonalAccessTokenRequest,
  PersonalAccessToken,
  PersonalAccessTokenAdminOperationResult,
  RenamePersonalAccessTokenRequest,
  RevokePersonalAccessTokenRequest,
  SynchronizePersonalAccessTokenOwnerRequest,
} from "@features/personal-access-tokens/types";

const personalAccessTokenListTag = { type: "PersonalAccessToken", id: "LIST" } as const;
const adminPersonalAccessTokenListTag = {
  type: "PersonalAccessToken",
  id: "ADMIN_LIST",
} as const;

const personalAccessTokensBuilder = (builder: EndpointBuilder<BaseQueryFn, string, "api">) => ({
  getMyPersonalAccessTokens: builder.query<PersonalAccessToken[] | undefined, void>({
    query: () => ({ url: personalAccessTokensRoutes.ownerCollection() }),
    transformResponse: (response: ServerResponse<PersonalAccessToken[]>) => response.data,
    providesTags: [personalAccessTokenListTag],
  }),
  getAdminPersonalAccessTokens: builder.query<
    PersonalAccessToken[] | undefined,
    AdminListPersonalAccessTokensRequest
  >({
    query: ({ contributorId }) => ({
      url: personalAccessTokensRoutes.adminCollection(),
      params: { "contributor-id": contributorId },
    }),
    transformResponse: (response: ServerResponse<PersonalAccessToken[]>) => response.data,
    providesTags: [adminPersonalAccessTokenListTag],
  }),
  issuePersonalAccessToken: builder.mutation<
    IssuedPersonalAccessToken | undefined,
    IssuePersonalAccessTokenRequest
  >({
    query: (body) => ({
      url: personalAccessTokensRoutes.ownerCollection(),
      method: "POST",
      body,
    }),
    transformResponse: (response: ServerResponse<IssuedPersonalAccessToken>) => response.data,
    invalidatesTags: [personalAccessTokenListTag],
  }),
  issueAdminPersonalAccessToken: builder.mutation<
    IssuedPersonalAccessToken | undefined,
    AdminIssuePersonalAccessTokenRequest
  >({
    query: (body) => ({
      url: personalAccessTokensRoutes.adminCollection(),
      method: "POST",
      body,
    }),
    transformResponse: (response: ServerResponse<IssuedPersonalAccessToken>) => response.data,
    invalidatesTags: [adminPersonalAccessTokenListTag],
  }),
  renamePersonalAccessToken: builder.mutation<
    PersonalAccessToken | undefined,
    RenamePersonalAccessTokenRequest & { readonly id: number }
  >({
    query: ({ id, name }) => ({
      url: personalAccessTokensRoutes.rename(id),
      method: "PUT",
      body: { name },
    }),
    transformResponse: (response: ServerResponse<PersonalAccessToken>) => response.data,
    invalidatesTags: [personalAccessTokenListTag],
  }),
  revokePersonalAccessToken: builder.mutation<
    PersonalAccessToken | undefined,
    RevokePersonalAccessTokenRequest & { readonly id: number }
  >({
    query: ({ id, reason }) => ({
      url: personalAccessTokensRoutes.revoke(id),
      method: "POST",
      ...(reason === undefined ? {} : { body: { reason } }),
    }),
    transformResponse: (response: ServerResponse<PersonalAccessToken>) => response.data,
    invalidatesTags: [personalAccessTokenListTag],
  }),
  revokeAdminPersonalAccessToken: builder.mutation<
    PersonalAccessToken | undefined,
    AdminRevokePersonalAccessTokenRequest
  >({
    query: ({ id, reason }) => ({
      url: personalAccessTokensRoutes.adminRevoke(id),
      method: "POST",
      ...(reason === undefined ? {} : { body: { reason } }),
    }),
    transformResponse: (response: ServerResponse<PersonalAccessToken>) => response.data,
    invalidatesTags: [adminPersonalAccessTokenListTag],
  }),
  deletePersonalAccessToken: builder.mutation<
    PersonalAccessToken | undefined,
    { readonly id: number }
  >({
    query: ({ id }) => ({
      url: personalAccessTokensRoutes.delete(id),
      method: "DELETE",
    }),
    transformResponse: (response: ServerResponse<PersonalAccessToken>) => response.data,
    invalidatesTags: [personalAccessTokenListTag],
  }),
  synchronizePersonalAccessTokenOwner: builder.mutation<
    PersonalAccessTokenAdminOperationResult | undefined,
    SynchronizePersonalAccessTokenOwnerRequest
  >({
    query: ({ lkUserId }) => ({
      url: personalAccessTokensRoutes.adminSynchronizeOwner(lkUserId),
      method: "POST",
    }),
    transformResponse: (response: ServerResponse<PersonalAccessTokenAdminOperationResult>) =>
      response.data,
    invalidatesTags: (_result, error) =>
      error ? [] : [personalAccessTokenListTag, adminPersonalAccessTokenListTag],
  }),
  transferPersonalAccessTokenUsage: builder.mutation<
    PersonalAccessTokenAdminOperationResult | undefined,
    void
  >({
    query: () => ({
      url: personalAccessTokensRoutes.adminTransferUsage(),
      method: "POST",
    }),
    transformResponse: (response: ServerResponse<PersonalAccessTokenAdminOperationResult>) =>
      response.data,
    invalidatesTags: (_result, error) =>
      error ? [] : [personalAccessTokenListTag, adminPersonalAccessTokenListTag],
  }),
  evictPersonalAccessTokenCache: builder.mutation<
    PersonalAccessTokenAdminOperationResult | undefined,
    void
  >({
    query: () => ({
      url: personalAccessTokensRoutes.adminEvictCache(),
      method: "POST",
    }),
    transformResponse: (response: ServerResponse<PersonalAccessTokenAdminOperationResult>) =>
      response.data,
    invalidatesTags: (_result, error) =>
      error ? [] : [personalAccessTokenListTag, adminPersonalAccessTokenListTag],
  }),
});

export const personalAccessTokensApi = apiSlice.injectEndpoints({
  endpoints: personalAccessTokensBuilder,
  overrideExisting: false,
});

export const {
  useGetAdminPersonalAccessTokensQuery,
  useLazyGetAdminPersonalAccessTokensQuery,
  useGetMyPersonalAccessTokensQuery,
  useLazyGetMyPersonalAccessTokensQuery,
  useIssueAdminPersonalAccessTokenMutation,
  useIssuePersonalAccessTokenMutation,
  useRevokeAdminPersonalAccessTokenMutation,
  useRenamePersonalAccessTokenMutation,
  useRevokePersonalAccessTokenMutation,
  useDeletePersonalAccessTokenMutation,
  useSynchronizePersonalAccessTokenOwnerMutation,
  useTransferPersonalAccessTokenUsageMutation,
  useEvictPersonalAccessTokenCacheMutation,
} = personalAccessTokensApi;
